import React from 'react';
import { Phone, MessageCircle, Users, Award, Shield, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { schoolInfo, images, whatsappMessage } from '../data/mockData';

const HeroSection = () => {
  const handleWhatsApp = () => {
    const url = `https://wa.me/${schoolInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(url, '_blank');
  };

  const trustBadges = [
    { icon: Users, label: "50+ Happy Parents" },
    { icon: Award, label: "15+ Years of Excellence" },
    { icon: Shield, label: "Safe & Secure Campus" }
  ];

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-24 pb-12 overflow-hidden">
      {/* Premium Gradient Background - Left Side */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-white to-transparent z-0" />
      
      {/* Subtle Abstract Shapes */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-yellow-400/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      {/* Right Side - Emotional Image with Gradient Blend */}
      <div className="absolute right-0 top-0 bottom-0 w-1/2 lg:w-2/5 hidden md:block">
        <div className="relative h-full">
          {/* Main Emotional Image */}
          <img 
            src={images.hero}
            alt="Happy children learning at Gyanodeepti School"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Gradient Overlay Blend */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-white/20" />
        </div>
      </div>

      {/* Content Container */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            {/* Premium Badge */}
            <div className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-6 py-3 rounded-full shadow-lg font-bold text-sm mb-8 animate-fadeInUp">
              <Clock className="w-4 h-4 mr-2" />
              Admissions Open for 2026-27
            </div>

            {/* Main Headline - Bold & Dominant */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-[1.1] mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
              Admissions Open –
            </h1>
            
            {/* Dominant Sub-headline */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] mb-8 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              <span className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 bg-clip-text text-transparent">
                Playgroup to Class 5
              </span>
            </h2>

            {/* Emotional Subheadline */}
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-10 font-medium animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
              Premier School & Day Care where your child's journey to excellence begins with care, safety, and joyful learning
            </p>

            {/* Trust Indicators - Horizontal Layout */}
            <div className="flex flex-wrap gap-6 mb-10 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
              {trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm px-4 py-3 rounded-xl shadow-md">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                    <badge.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-gray-800">{badge.label}</span>
                </div>
              ))}
            </div>

            {/* CTA Section - Primary & Secondary */}
            <div className="space-y-4 animate-fadeInUp" style={{ animationDelay: '0.5s' }}>
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Primary CTA - Call Now */}
                <a href={`tel:${schoolInfo.phone}`} className="flex-1 sm:flex-initial">
                  <Button 
                    size="lg"
                    className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold text-lg px-12 py-8 rounded-2xl shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105 hover:-translate-y-1 border-2 border-blue-700"
                  >
                    <Phone className="w-6 h-6 mr-3" />
                    Call Now - Get Admission Info
                  </Button>
                </a>
                
                {/* Secondary CTA - WhatsApp */}
                <Button 
                  size="lg"
                  onClick={handleWhatsApp}
                  className="w-full sm:w-auto bg-white hover:bg-gray-50 text-green-600 font-semibold text-lg px-10 py-8 rounded-2xl shadow-lg border-2 border-green-600 hover:border-green-700 transition-all duration-300 hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Enquiry
                </Button>
              </div>

              {/* Urgency Microcopy */}
              <div className="flex items-center space-x-2 text-gray-700">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <p className="text-sm md:text-base font-semibold">
                  Limited seats available for 2026–27 • Hurry!
                </p>
              </div>
            </div>

            {/* Additional Trust Line */}
            <div className="mt-8 pt-8 border-t border-gray-200 animate-fadeInUp" style={{ animationDelay: '0.6s' }}>
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="font-medium">Experienced Faculty</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="font-medium">Activity-Based Learning</span>
                </div>
              </div>
            </div>

            {/* School Timings */}
            <div className="mt-6 bg-blue-50 border-l-4 border-blue-600 rounded-r-xl p-4 animate-fadeInUp" style={{ animationDelay: '0.7s' }}>
              <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                School Timings
              </h3>
              <div className="space-y-1 text-sm text-gray-700">
                <p><strong>Summer Session:</strong> 9:00 AM – 12:30 PM</p>
                <p><strong>Winter Session:</strong> 9:30 AM – 1:00 PM</p>
                <p className="text-xs text-gray-600 mt-2">Monday - Saturday</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Image - Below content on small screens */}
      <div className="md:hidden relative w-full h-64 mt-8">
        <img 
          src={images.hero}
          alt="Happy children at Gyanodeepti School"
          className="w-full h-full object-cover rounded-3xl"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-white/20 rounded-3xl" />
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block">
        <div className="w-6 h-10 border-2 border-blue-600/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-blue-600/70 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;